import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ExternalLink, FileText, Check, CheckCircle2, XCircle, Clock, Send, Calendar, AlertTriangle, History, Star, Tag } from 'lucide-react';
import { ClipTagsPanel } from '@/components/ClipTagsPanel';
import { TagMark } from '@/components/TagMark';
import EstimateTargetSheet from '@/components/EstimateTargetSheet';
import '@/styles/clip-tags.css';
import '@/styles/tag-mark.css';
import '@/styles/receiving.css';
import { makeUnifiedPayload, setUnifiedDragPayload } from '../utils/keymadeItemPayload';
import { getCurrentUserId } from '../utils/currentUser';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// CL-3b — the firm's star. Anyone on the team may add theirs, and the clip is
// a firm favourite while any star is on it: that is what keeps it out of the
// bin when the project is cleared down.
export const ClipStar = ({ clip, currentUserId, onChange, testIdSuffix = '' }) => {
  const [starred, setStarred] = useState(clip.starred_by || []);
  const [busy, setBusy] = useState(false);
  // Pages that don't pass the caller down still show the star as the person's
  // own when it is theirs.
  const me = currentUserId || getCurrentUserId();
  const mine = starred.some(s => s?.user_id === me);
  const names = starred.map(s => s?.name).filter(Boolean).join(', ');

  const toggle = async (e) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/clips/${clip.id}/star`);
      setStarred(res.data.starred_by || []);
      onChange?.(res.data.starred_by || []);
    } catch (error) {
      console.error('Error starring clip:', error);
      toast.error('Could not change the star');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      data-action
      onClick={toggle}
      disabled={busy}
      className="kso-btn"
      // CL-5 / CL-5b — the title says what the star is for, then who starred it.
      title={"Star it for the firm's library. Starred items go back to the Main Clip Library instead of being deleted."
        + (names ? `\nStarred by ${names}` : '')}
      aria-label={names ? `Starred by ${names}` : 'Star it for the firm\'s library'}
      aria-pressed={mine}
      data-testid={`clip-star${testIdSuffix}-${clip.id}`}
    >
      <Star className="w-3.5 h-3.5" strokeWidth={1.5}
        style={{ fill: mine ? 'currentColor' : 'none' }} />
      {starred.length > 1 && <span>{starred.length}</span>}
    </button>
  );
};

// CL-5 — the tick that puts a material on the estimate, and the number the
// ledger gives it. One line per clip; unticking retires that number for good.
// SP-2 — on the clip page the number is a door to its estimate line; on a
// card it stays a plain number, because the card foot is not to change.
export const EstimateTick = ({ clip, onUpdate, compact = false,
                               estimateItem = null }) => {
  const [on, setOn] = useState(clip.include_in_proposal === true);
  const [specNum, setSpecNum] = useState(clip.spec_num || null);
  // CL-2a — after the client says yes, an addition is a change order line.
  const [coNumber, setCoNumber] = useState(clip.change_order_number || null);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  // CL-6 — where the line landed, and the "Put it on" sheet when there is a choice.
  const [estTarget, setEstTarget] = useState(clip.estimate_target || null);
  const [sheet, setSheet] = useState(null);
  const targetKey = `km_est_target:${clip.project_id}`;

  useEffect(() => {
    setOn(clip.include_in_proposal === true);
    setSpecNum(clip.spec_num || null);
    setCoNumber(clip.change_order_number || null);
    setEstTarget(clip.estimate_target || null);
  }, [clip.include_in_proposal, clip.spec_num, clip.change_order_number, clip.estimate_target]);

  const post = async (next, target) => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/clips/${clip.id}/estimate-line`, target ? { on: next, target } : { on: next });
      setOn(res.data.on);
      setEstTarget(res.data.target || null);
      setSpecNum(res.data.spec_num || null);
      setCoNumber(res.data.change_order_number || null);
      // SS-2 — the first tick on a project with no estimate makes the draft.
      if (res.data.created_estimate) {
        toast.success(`Draft estimate created${res.data.spec_num
          ? ` · added as ${res.data.spec_num}` : ''}`);
      } else if (res.data.change_order_number) {
        toast.success(`Added to ${res.data.change_order_number}`);
      }
      onUpdate?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' && detail
        ? detail : 'Could not change the estimate');
    } finally {
      setBusy(false);
    }
  };
  const send = async (next) => {
    if (!next) return post(false);
    let targets = [];
    try {
      const r = await axios.get(`${API}/clips/${clip.id}/estimate-targets`);
      targets = r.data.targets || [];
    } catch { targets = []; }
    if (targets.length <= 1) return post(true);
    setSheet(targets);
    return undefined;
  };
  const pickTarget = (t) => {
    setSheet(null);
    if (!t) return;
    try { window.sessionStorage.setItem(targetKey, t.id); } catch { /* private mode */ }
    post(true, { kind: t.kind, id: t.id });
  };
  const rememberedId = (() => { try { return window.sessionStorage.getItem(targetKey); } catch { return null; } })();

  const noProject = !clip.project_id;
  // CL-3 — the signed contract is the only lock. Before it, the tick is an
  // estimate line with its number; after it, an addition is a change.
  const signed = clip.contract_signed === true;
  // CL-2b — once the change order is approved its line is an estimate line, so
  // the clip reads as on the estimate and keeps the change order's number too.
  const onEstimate = Boolean(specNum);
  const label = noProject
    ? 'Pick a project to add it to the estimate'
    : !on
      ? (signed ? 'Add as a change' : 'Add to estimate')
      : onEstimate
        ? `On ${estTarget && estTarget.kind === 'estimate' && estTarget.name ? estTarget.name : 'the estimate'} · `
        : coNumber ? 'On change order ' : 'Ready for the estimate';

  return (
    <>
      {sheet && <EstimateTargetSheet targets={sheet} initialId={rememberedId} onPick={pickTarget} onClose={() => setSheet(null)} />}
      <label className="kcc-foot__est cursor-pointer" data-checkbox>
        <Checkbox
          checked={on}
          disabled={busy || noProject}
          onCheckedChange={(next) => (next ? send(true) : setAsking(true))}
          data-testid={`clip-checkbox-${clip.id}`}
        />
        <span className="kcc-foot__est-text"
          title={`${label}${on && (specNum || coNumber)
            ? (onEstimate ? specNum : coNumber) : ''}`}
          data-testid={noProject
            ? `clip-estimate-noproject-${clip.id}` : `clip-estimate-label-${clip.id}`}>
          {label}
          {on && (specNum || coNumber) && (
            <b className="kcc-spec">
              {onEstimate && estimateItem?.estimate_id ? (
                <a className="kcc-spec-link"
                  href={`/projects/${clip.project_id}/estimates/${estimateItem.estimate_id}`
                    + `?item=${estimateItem.item_id}`}
                  onClick={(e) => e.stopPropagation()}
                  data-testid="clip-estimate-link">{specNum}</a>
              ) : (onEstimate ? specNum : coNumber)}
              {/* CL-3 — a stale parking on an unsigned project is not shown. */}
              {onEstimate && coNumber && signed ? ` · ${coNumber}` : ''}
            </b>
          )}
        </span>
      </label>
      <AlertDialog open={asking} onOpenChange={setAsking}>
        <AlertDialogContent data-testid={`clip-untick-dialog-${clip.id}`}
          onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove from the estimate?</AlertDialogTitle>
            <AlertDialogDescription>
              {clip.title || clip.product_name} · {specNum || 'no number yet'}. The line comes
              off the estimate and {specNum || 'the number'} is retired in the ledger. The clip
              stays on the project; ticking it again gives it a new number.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`clip-untick-keep-${clip.id}`}>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={() => send(false)}
              data-testid={`clip-untick-remove-${clip.id}`}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

// SP-1 — the tag mark: a card says, quietly, that this material has been
// labelled, how many and how far along. Clicking it opens the clip's tags.
export const ClipTagMark = ({ clip, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const count = Number(clip.tag_count || 0);
  if (!count) return null;
  const state = clip.tag_state || 'printed';
  return (
    <>
      <button type="button" data-action className="km-tag-btn"
        title={`${count} printed tag${count > 1 ? 's' : ''} · ${state.replace('_', ' ')}`}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid={`clip-tag-mark-${clip.id}`}>
        <TagMark n={count} state={state} />
      </button>
      {open ? (
        <ClipTagsPanel clip={clip} onUpdate={onUpdate}
          onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
};

// PR-1d — the whereabouts, on their own quiet row under the estimate line.
const RECEIVE_PLACE = { office: 'Office', site: 'Site', warehouse: 'Warehouse',
  vendor: 'Vendor holds', contractor: 'Contractor' };

export const whereaboutsOf = (clip) => {
  const held = clip.receiving;
  if (!held?.state) return '';
  const where = RECEIVE_PLACE[held.where] || held.where || '';
  return ['Received', where, held.location].filter(Boolean).join(' · ');
};

export const ClipReceivedLine = ({ clip }) => {
  const said = whereaboutsOf(clip);
  if (!said) return null;
  const late = ['damaged', 'short'].includes(clip.receiving.state);
  return (
    <div className={`kcc-foot__status${late ? ' is-late' : ''}`}
      title={`${clip.receiving.count} of ${clip.receiving.of} · ${clip.receiving.state}`}
      data-testid={`clip-foot-status-${clip.id}`}>
      {said}
    </div>
  );
};

export const ClipCard = ({ clip, viewMode, isSelected, onToggleSelect, onUpdate, showSelection = false, onApprove, onSubmitForApproval, showApprovalActions = false, onViewHistory, onTag, currentUserId, bulkSelectable = false, bulkChecked = false, onBulkToggle, density = 'comfortable' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const compact = density === 'compact';

  const handleCardClick = (e) => {
    if (e.target.closest('[data-checkbox]') || e.target.closest('[data-action]') || e.target.closest('button')) {
      return;
    }
    // Carry the current route forward as `state.from` so ClipDetail's "Back to
    // Library" can return the user to the SAME view they came from — e.g. the
    // project hub's Clips tab — instead of dumping them on My Desk.
    navigate(`/clips/${clip.id}`, {
      state: { from: location.pathname + location.search },
    });
  };

  const hasPDFs = clip.pdf_files && clip.pdf_files.length > 0;
  const isIncluded = clip.include_in_proposal === true;
  const approvalStatus = clip.client_approval_status || 'pending';
  const hasSpecNum = !!clip.spec_num;

  // Get approval status badge color
  const getApprovalBadge = () => {
    switch (approvalStatus) {
      case 'approved':
        return (
          <Badge className="bg-green-500 text-white text-[10px] flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-500 text-white text-[10px] flex items-center gap-1">
            <XCircle className="w-3 h-3" />
            Rejected
          </Badge>
        );
      case 'submitted':
        return (
          <Badge className="bg-purple-500 text-white text-[10px] flex items-center gap-1">
            <Send className="w-3 h-3" />
            Awaiting Client
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="text-[10px] flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </Badge>
        );
    }
  };

  // Check if due date is approaching or overdue
  const getDueDateStatus = () => {
    if (!clip.decision_due_date) return null;
    const dueDate = new Date(clip.decision_due_date);
    const today = new Date();
    const daysUntil = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntil < 0) {
      return { status: 'overdue', label: `${Math.abs(daysUntil)} days overdue`, color: 'text-red-500' };
    } else if (daysUntil === 0) {
      return { status: 'today', label: 'Due today', color: 'text-amber-500' };
    } else if (daysUntil <= 3) {
      return { status: 'soon', label: `Due in ${daysUntil} day${daysUntil > 1 ? 's' : ''}`, color: 'text-amber-500' };
    }
    return { status: 'ok', label: `Due ${dueDate.toLocaleDateString()}`, color: 'text-gray-500' };
  };

  const dueDateStatus = getDueDateStatus();

  // CL-5 — the spec category, as its prefix. A clip with no category cannot be
  // numbered properly, so the chip says so and leads to the spec sheet where it
  // is set.
  const openSpecSheet = (e) => {
    e.stopPropagation();
    navigate(`/clips/${clip.id}`, { state: { from: location.pathname + location.search } });
  };

  const categoryChip = clip.spec_category ? (
    <Badge variant="outline" className="text-[10px]"
      data-testid={`clip-category-chip-${clip.id}`}>
      {clip.spec_category}
    </Badge>
  ) : (
    <Badge variant="outline" data-action onClick={openSpecSheet}
      className="text-[10px] border-dashed cursor-pointer truncate max-w-full"
      title="Uncategorized — pick a category on the spec sheet"
      data-testid={`clip-category-missing-${clip.id}`}>
      Uncategorized · pick a category
    </Badge>
  );

  const shortApproval = { approved: 'Appr.', rejected: 'Rej.', submitted: 'Sent' }[approvalStatus] || 'Pend.';

  // RS-7 — whether the paper behind this clip is stamped, and which submittal
  // said so. The chip leads to the submittal, where the stamp is.
  const SUBMITTAL_FACE = {
    approved: ['Submittal approved', 'ok'],
    revise: ['Revise & resubmit', 'info'],
    rejected: ['Rejected', 'late'],
  };
  const submittalFace = SUBMITTAL_FACE[clip.submittal_state];
  const openSubmittal = (e) => {
    e.stopPropagation();
    navigate(`/projects/${clip.project_id}/rfis?tab=submittals&submittal=${clip.submittal?.id || ''}`);
  };
  const submittalChip = submittalFace ? (
    <Badge variant="outline" data-action onClick={openSubmittal}
      className="text-[10px] cursor-pointer"
      style={{ borderColor: `var(--km-${submittalFace[1]})`, color: `var(--km-${submittalFace[1]})` }}
      title={`${submittalFace[0]} · ${clip.submittal?.number || ''}`}
      data-testid={`clip-submittal-chip-${clip.id}`}>
      {`${submittalFace[0]}${clip.submittal?.number ? ` · ${clip.submittal.number}` : ''}`}
    </Badge>
  ) : null;

  // CL-5 — the footer row: the estimate tick at the left, the firm's star at the
  // right end, in both sizes.
  const footerRow = (
    <div className={`kcc-foot ${compact ? 'mt-2' : 'mt-3'}`}
      onClick={(e) => e.stopPropagation()}
      data-testid={`clip-foot-${clip.id}`}
    >
      <div className="kcc-foot__row" data-testid={`clip-footer-${clip.id}`}>
        {showSelection && <EstimateTick clip={clip} onUpdate={onUpdate} compact={compact} />}
        {!showSelection && <span className="flex-1" />}
        <ClipTagMark clip={clip} onUpdate={onUpdate} />
        <span className="shrink-0">
          <ClipStar clip={clip} currentUserId={currentUserId} onChange={() => onUpdate?.()} />
        </span>
      </div>
      <ClipReceivedLine clip={clip} />
    </div>
  );

  const bulkBox = bulkSelectable && (
    <div className="absolute top-4 left-4 z-30" data-checkbox onClick={(e) => e.stopPropagation()}>
      <div className="bg-background/90 rounded p-1">
        <Checkbox
          checked={bulkChecked}
          onCheckedChange={() => onBulkToggle?.(clip.id)}
          data-testid={`clip-bulk-checkbox-${clip.id}`}
        />
      </div>
    </div>
  );

  if (viewMode === 'list') {
    return (
      <div
        className="border border-border bg-card p-6 hover:border-primary/50 transition-all duration-300 cursor-pointer"
        onClick={handleCardClick}
        data-testid={`clip-card-${clip.id}`}
      >
        <div className="flex items-start gap-6">
          {showSelection && (
            <div data-checkbox>
              <Checkbox
                checked={isIncluded}
                onCheckedChange={() => onToggleSelect(clip.id, !isIncluded)}
                data-testid={`clip-checkbox-${clip.id}`}
              />
            </div>
          )}
          {clip.images && clip.images.length > 0 && (
            <div className="w-32 h-32 border border-border bg-muted flex-shrink-0 overflow-hidden">
              <img
                src={clip.primary_image || clip.images[0]}
                alt={clip.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="font-heading text-xl mb-2 truncate" data-testid={`clip-title-${clip.id}`}>{clip.title}</h3>
            <div className="space-y-2">
              {clip.product_type && (
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Type:</span> {clip.product_type}
                </p>
              )}
              {clip.manufacturer && (
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Manufacturer:</span> {clip.manufacturer}
                </p>
              )}
              {hasPDFs && (
                <p className="text-sm text-accent flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  {clip.pdf_files.length} PDF{clip.pdf_files.length > 1 ? 's' : ''}
                </p>
              )}
              {clip.tags && clip.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {clip.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <div
        className={`group relative border bg-card hover:border-primary/50 transition-all duration-300 overflow-hidden cursor-pointer rounded-lg ${isIncluded ? 'border-green-500 border-2' : 'border-border'}`}
        onClick={handleCardClick}
        data-testid={`clip-card-${clip.id}`}
      >
        {bulkBox}
        <div className="bg-muted overflow-hidden" style={{ aspectRatio: '4 / 3' }}>
          {clip.images && clip.images.length > 0 ? (
            <img
              src={clip.primary_image || clip.images[0]}
              alt={clip.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center p-2">
              <p className="font-heading text-xs text-center line-clamp-2"
                style={{ color: 'var(--km-brand)' }}>
                {clip.title || clip.product_name}
              </p>
            </div>
          )}
        </div>
        <div className="p-2">
          <h3 className="font-heading text-sm truncate text-foreground"
            title={clip.title || clip.product_name}
            data-testid={`clip-title-${clip.id}`}>
            {clip.title || clip.product_name}
          </h3>
          <div className="mt-1 flex items-center gap-1 flex-wrap">
            <Badge variant="secondary" className="text-[10px]"
              data-testid={`clip-approval-chip-${clip.id}`}>
              {shortApproval}
            </Badge>
            {submittalChip}
            {hasSpecNum ? (
              <Badge variant="outline" className="text-[10px]"
                style={{ fontFamily: 'var(--km-tech)', color: 'var(--km-brand)' }}
                data-testid={`clip-specnum-chip-${clip.id}`}>
                {clip.spec_num}
              </Badge>
            ) : categoryChip}
          </div>
          {footerRow}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`group relative border bg-card hover:border-[#BC5D41]/50 transition-all duration-500 overflow-hidden cursor-pointer rounded-lg ${isIncluded ? 'border-green-500 border-2' : 'border-border'}`}
      onClick={handleCardClick}
      draggable
      onDragStart={(e) => {
        // Phase C2 — clips emit the unified drag payload so they can
        // be dropped onto any Work Table Cell/Cluster/canvas as a
        // native card. Multi-tenant safety: `GET /api/clips` is
        // already company-scoped, so this ClipCard only renders for
        // items owned by the caller's company.
        try {
          const images = clip.images || [];
          const thumb = clip.primary_image || images[0] || clip.image_url || null;
          setUnifiedDragPayload(e, makeUnifiedPayload({
            item_id: clip.id,
            source_store: 'clips',
            type: 'clip',
            name: clip.title || clip.name || clip.product_name,
            thumbnail: thumb,
            source_url: clip.source_url || clip.url || null,
            spec_num: clip.spec_num || null,
          }));
          e.dataTransfer.effectAllowed = 'copy';
        } catch (_err) { void _err; }
      }}
      data-testid={`clip-card-${clip.id}`}
    >
      {/* CL-3b / CL-5 — the bulk checkbox when Select is on; the firm's star
          now lives in the footer row, not over the image. */}
      {bulkBox}
      {hasPDFs && (
        <div className="absolute top-4 right-4 z-10 bg-[#70170F] text-white px-3 py-1 flex items-center gap-2 text-xs font-medium rounded">
          <FileText className="w-3 h-3" strokeWidth={1.5} />
          {clip.pdf_files.length} PDF{clip.pdf_files.length > 1 ? 's' : ''}
        </div>
      )}
      {clip.images && clip.images.length > 0 ? (
        <div className="aspect-square bg-[#BC5D41]/10 overflow-hidden">
          <img
            src={clip.primary_image || clip.images[0]}
            alt={clip.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
              const fallback = document.createElement('div');
              fallback.className = 'text-center p-4';
              fallback.innerHTML = `<p class="text-[#70170F] font-heading text-sm font-medium">${clip.title?.substring(0, 30) || 'Product'}...</p><p class="text-[#70170F]/60 text-xs mt-1">${clip.brand || clip.manufacturer || 'No image'}</p>`;
              e.target.parentElement.appendChild(fallback);
            }}
          />
        </div>
      ) : (
        <div className="aspect-square bg-[#BC5D41]/10 flex items-center justify-center p-4">
          <div className="text-center">
            <p className="text-[#70170F] font-heading text-sm font-medium line-clamp-2">{clip.title || clip.product_name}</p>
            <p className="text-[#70170F]/60 text-xs mt-1">{clip.brand || clip.manufacturer || 'No image'}</p>
          </div>
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between mb-1">
          <h3 className="font-heading text-base line-clamp-2 text-foreground flex-1" data-testid={`clip-title-${clip.id}`}>{clip.title || clip.product_name}</h3>
          {hasSpecNum && (
            <Badge className="bg-[#70170F] text-white text-[10px] ml-2 shrink-0">
              {clip.spec_num}
            </Badge>
          )}
        </div>
        {clip.brand && (
          <p className="text-xs text-[#BC5D41] mb-1">{clip.brand}</p>
        )}
        {clip.room_location && (
          <p className="text-xs text-muted-foreground mb-2">{clip.room_location}</p>
        )}
        {clip.product_type && (
          <p className="text-xs text-muted-foreground mb-2">{clip.product_type}</p>
        )}
        
        {/* Approval Status Badge & Due Date */}
        <div className="mb-2 flex items-center gap-2 flex-wrap">
          {getApprovalBadge()}
          {categoryChip}
          {submittalChip}
          {dueDateStatus && approvalStatus === 'submitted' && (
            <span className={`text-[10px] flex items-center gap-1 ${dueDateStatus.color}`}>
              {dueDateStatus.status === 'overdue' && <AlertTriangle className="w-3 h-3" />}
              {dueDateStatus.status !== 'overdue' && <Calendar className="w-3 h-3" />}
              {dueDateStatus.label}
            </span>
          )}
        </div>
        
        {/* Approval Actions */}
        {showApprovalActions && approvalStatus === 'pending' && onSubmitForApproval && (
          <div className="mb-2" data-action onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              variant="outline"
              className="w-full text-xs h-7 border-purple-300 text-purple-600 hover:bg-purple-50"
              onClick={() => onSubmitForApproval(clip)}
            >
              <Send className="w-3 h-3 mr-1" />
              Submit for Approval
            </Button>
          </div>
        )}
        
        {showApprovalActions && approvalStatus === 'submitted' && onApprove && (
          <div className="mb-2 flex gap-1" data-action onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-xs h-7 border-green-300 text-green-600 hover:bg-green-50"
              onClick={() => onApprove(clip.id, 'approved')}
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="flex-1 text-xs h-7 border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => onApprove(clip.id, 'rejected')}
            >
              <XCircle className="w-3 h-3 mr-1" />
              Reject
            </Button>
          </div>
        )}
        
        {/* View History Button - show for any non-pending status */}
        {showApprovalActions && onViewHistory && (approvalStatus === 'approved' || approvalStatus === 'rejected' || approvalStatus === 'submitted') && (
          <div className="mb-2" data-action onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              variant="ghost"
              className="w-full text-xs h-6 text-muted-foreground hover:text-[#70170F]"
              onClick={() => onViewHistory(clip)}
            >
              <History className="w-3 h-3 mr-1" />
              View History
            </Button>
          </div>
        )}
        
        {/* DP276-M2 — a material on a project can be tagged for the carton it
            arrives in. A library clip belongs to no project, so it cannot. */}
        {clip.project_id && onTag && (
          <div className="mb-2" data-action onClick={(e) => e.stopPropagation()}>
            <button type="button" className="kso-btn" onClick={() => onTag(clip)}
              data-testid={`clip-tag-${clip.id}`}>Tag this material…</button>
          </div>
        )}

        {clip.tags && clip.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {clip.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="secondary" className="text-[10px] bg-[#BC5D41]/10 text-[#70170F] border-0">
                {tag}
              </Badge>
            ))}
            {clip.tags.length > 3 && (
              <Badge variant="secondary" className="text-[10px] bg-[#BC5D41]/10 text-[#70170F] border-0">
                +{clip.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
        
        {/* CL-5 — the estimate tick and the firm's star, one footer row */}
        {footerRow}
      </div>
    </div>
  );
};

export default ClipCard;